import { useQuery } from "@tanstack/react-query";
import {
  CountryCode,
  StationInformation,
  StationResponse,
} from "@vygruppen/vy-train-map";
import { getBackendUrl } from "api/common";
import { queryFnGET } from "api/tanStackQuery/helpers";
import { queryClient } from "api/tanStackQuery/queryClient";
import { useEffect } from "react";
import { createToast } from "shared/components/Spor";
import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";
import { StationInformationMessage } from "websocket/websocketSharedWorkerTypes";

const stationInformationQueryKey = (countryCode?: CountryCode) => [
  "stationInfo",
  countryCode,
];

const updateStationInfo = (
  message: StationInformation,
  prev: StationInformation[],
): StationInformation[] => {
  if (!message.event.active) {
    return prev.filter((info) => info.uuid !== message.uuid);
  }

  return [message, ...prev.filter((info) => info.uuid !== message.uuid)];
};

const handleStationWebsocketMessage = (
  websocketMessage: StationInformationMessage,
) => {
  const { message } = websocketMessage;
  const { countryCode } = message.event;
  queryClient.setQueryData<StationResponse>(
    stationInformationQueryKey(countryCode),
    (prev) =>
      prev
        ? {
            station_information: updateStationInfo(
              message,
              prev.station_information,
            ),
          }
        : {
            station_information: message.event.active ? [message] : [],
          },
  );
};

export const useStationInformation = () => {
  const countryCode = useCountryCodeForDropsRole();

  const {
    data: stationInfo,
    isSuccess: isStationInfoSuccess,
    status,
  } = useQuery({
    queryKey: stationInformationQueryKey(countryCode),
    retry: 1,
    queryFn: ({ signal }) =>
      queryFnGET<StationResponse>({
        signal,
        url: `${getBackendUrl()}/station-information/trainmap-station-events?countryCode=${countryCode}`,
      }),
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (status === "error") {
      createToast({
        variant: "error",
        text: "Kunne ikke hente stasjonshendelser",
      });
    }
  }, [status]);

  return {
    stationInfo,
    isStationInfoSuccess,
    handleStationWebsocketMessage,
  };
};
